'use client';

import dynamic from 'next/dynamic';
import { ReactNode, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Typography from '@alltrails/core/components/Typography';
import type TrailDetails from '@alltrails/core/types/TrailDetails';
import Link from '@alltrails/denali/components/Link';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import ChevronSm from '@alltrails/denali/icons/ChevronSm';
import Closure from '@alltrails/denali/icons/Closure';
import ClosureFilled from '@alltrails/denali/icons/ClosureFilled';
import ErrorIcon from '@alltrails/denali/icons/Error';
import { TrailInfo } from '@alltrails/di-tooling';
import type { Group } from '@alltrails/groups';
import { type Alert, AlertType } from '@alltrails/types/Alert';
import isClosureOrDangerAlertActive from '../../utils/isClosureOrDangerAlertActive';
import styles from './HighPriorityAlerts.module.scss';

const AlertsModal = dynamic(() => import('@/components/AlertsModal/AlertsModal'), { ssr: false });

type Props = {
  alerts: Array<Alert & { description: ReactNode }>;
  isAdmin?: boolean;
  trailDetails?: TrailDetails;
  trailInfo?: TrailInfo;
  userGroups?: Group[];
  isPLPUser?: boolean;
};

export default function HighPriorityAlerts({ alerts, isAdmin, trailDetails, trailInfo, isPLPUser, userGroups }: Props) {
  const [isAlertsModalOpen, setIsAlertsModalOpen] = useState(false);
  const isMobile = useIsMobileSizedScreen();
  const activeAlerts = alerts.filter(isClosureOrDangerAlertActive);

  if (activeAlerts.length === 0 || activeAlerts.every(alert => alert.alertType !== AlertType.CLOSURE && alert.alertType !== AlertType.DANGER)) {
    return null;
  }

  const hasClosureAlert = activeAlerts.some(alert => alert.alertType === AlertType.CLOSURE);
  const firstDangerAlert = activeAlerts.find(alert => alert.alertType === AlertType.DANGER);
  const showDangerAlert = !hasClosureAlert && firstDangerAlert;
  return (
    <>
      {!isMobile ? (
        <div className={styles.inlineAlerts}>
          <ClosureFilled color="--color-text-error" size="sm" />
          <Link className={styles.inlineAlertsLink} onClick={() => setIsAlertsModalOpen(true)}>
            {hasClosureAlert ? <FormattedMessage defaultMessage="Trail closed" /> : <FormattedMessage defaultMessage="Danger" />}
          </Link>
          {showDangerAlert && firstDangerAlert.title}
          {!showDangerAlert && alerts.length > 1 && (
            <FormattedMessage
              defaultMessage="{count, plural, one {+ # other alert} other {+ # other alerts}}"
              values={{ count: alerts.length - 1 }}
            />
          )}
          <div className={styles.dividerDot}>·</div>
        </div>
      ) : (
        <button
          className={styles.alerts}
          onClick={() => {
            setIsAlertsModalOpen(true);
          }}
        >
          <div className={styles.icon}>{hasClosureAlert ? <Closure color="--color-text-error" /> : <ErrorIcon color="--color-text-error" />}</div>
          <div className={styles.text}>
            <Typography className={styles.link} component="div" variant="text200">
              {hasClosureAlert ? <FormattedMessage defaultMessage="Trail closed" /> : <FormattedMessage defaultMessage="Danger" />}
            </Typography>
            {(showDangerAlert || alerts.length > 1) && (
              <div className={styles.sub}>
                {showDangerAlert ? (
                  firstDangerAlert.title
                ) : (
                  <FormattedMessage
                    defaultMessage="{count, plural, one {+ # other alert} other {+ # other alerts}}"
                    values={{ count: alerts.length - 1 }}
                  />
                )}
              </div>
            )}
          </div>
          <ChevronSm className={styles.chevron} orientation="right" />
        </button>
      )}
      {isAlertsModalOpen && trailDetails && (
        <AlertsModal
          isAdmin={isAdmin}
          isPLPUser={isPLPUser}
          onClose={() => setIsAlertsModalOpen(false)}
          trailDetails={trailDetails}
          trailInfo={trailInfo}
          userGroups={userGroups}
        />
      )}
    </>
  );
}
